import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useVault } from "./VaultContext";

const FILE_PATH = "forum/follows.json";
const LS_KEY = "mudoForumFollows";

const ForumSubscriptionsContext = createContext(null);

function readLocal() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    if (parsed && typeof parsed === "object") return parsed;
  } catch {}
  return {};
}

export function ForumSubscriptionsProvider({ children }) {
  const { ready, getJSON, setJSON } = useVault() || {};
  const [subs, setSubs] = useState(() => readLocal());
  const hydratedRef = useRef(false);

  // Hydrate from vault (or local fallback)
  useEffect(() => {
    if (!ready) return;
    let active = true;
    (async () => {
      try {
        const disk = (await getJSON?.(FILE_PATH, null)) || null;
        if (active && disk && typeof disk === "object") {
          setSubs(disk);
        } else if (active) {
          setSubs(readLocal());
        }
      } finally {
        hydratedRef.current = true;
      }
    })();
    return () => {
      active = false;
    };
  }, [ready, getJSON]);

  // Persist to localStorage + vault
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(subs));
    } catch {}
    if (!ready || !hydratedRef.current) return;
    (async () => {
      try {
        await setJSON?.(FILE_PATH, subs);
      } catch {}
    })();
  }, [subs, ready, setJSON]);

  const follow = useCallback((threadId) => {
    if (!threadId) return;
    setSubs((prev) => ({
      ...prev,
      [threadId]: { followed: true, followedAt: new Date().toISOString() },
    }));
  }, []);

  const unfollow = useCallback((threadId) => {
    if (!threadId) return;
    setSubs((prev) => {
      const next = { ...prev };
      if (next[threadId]) next[threadId] = { ...next[threadId], followed: false };
      else next[threadId] = { followed: false, followedAt: null };
      return next;
    });
  }, []);

  const toggleFollow = useCallback(
    (threadId) => {
      if (!threadId) return;
      setSubs((prev) => {
        const current = prev[threadId]?.followed;
        const next = { ...prev };
        next[threadId] = {
          followed: !current,
          followedAt: !current ? new Date().toISOString() : prev[threadId]?.followedAt || null,
        };
        return next;
      });
    },
    []
  );

  const isFollowing = useCallback((threadId) => !!subs?.[threadId]?.followed, [subs]);

  const followedThreadIds = useMemo(
    () => Object.keys(subs || {}).filter((id) => subs[id]?.followed),
    [subs]
  );

  const value = useMemo(
    () => ({
      isFollowing,
      toggleFollow,
      follow,
      unfollow,
      followedThreadIds,
    }),
    [isFollowing, toggleFollow, follow, unfollow, followedThreadIds]
  );

  return (
    <ForumSubscriptionsContext.Provider value={value}>
      {children}
    </ForumSubscriptionsContext.Provider>
  );
}

export function useForumSubscriptions() {
  const ctx = useContext(ForumSubscriptionsContext);
  if (ctx) return ctx;
  return {
    isFollowing: () => false,
    toggleFollow: () => {},
    follow: () => {},
    unfollow: () => {},
    followedThreadIds: [],
  };
}
